// =============================================================================
// Dashboard Views: Profile, Help, WeChat, Credits
// Extracted from make_album/dashboard.html inline JS (lines ~3620-3961)
// =============================================================================

import type { User, Address } from './dashboard-types';
import { loadAddresses, saveAddressesToCache, createAddressOnServer, updateAddressOnServer, deleteAddressOnServer } from './dashboard-data';
import { getCreditsPerYuan } from './dashboard-orders';
import { showToast, showPrompt, showConfirm, showWarning, escapeHtml, formatTime } from './dashboard-ui';
import { updateAvatarUI, clearLocalAccountData, renderView } from './dashboard-auth';

// ---------------------------------------------------------------------------
// External functions not yet extracted into their own TS modules.
// The main entry point (or a future dashboard-works module) must call
// `setShowCreditsHistory(fn)` / `setShowAddressModal(fn)` to wire these up.
// ---------------------------------------------------------------------------

let _showCreditsHistory: (() => void) | null = null;

export function setShowCreditsHistory(fn: () => void): void {
  _showCreditsHistory = fn;
}

interface AddressFormData {
  name: string;
  phone: string;
  province: string;
  city: string;
  district: string;
  detail: string;
}

let _showAddressModal: ((current: Partial<AddressFormData> | null) => Promise<AddressFormData | null>) | null = null;

export function setShowAddressModal(fn: (current: Partial<AddressFormData> | null) => Promise<AddressFormData | null>): void {
  _showAddressModal = fn;
}

// ========================
// View: Profile
// ========================

export function renderProfile(container: HTMLElement): void {
  const user: Partial<User> = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const users: Partial<User>[] = JSON.parse(localStorage.getItem('registeredUsers_' + (user.id || 'anonymous')) || '[]');
  const addresses: Address[] = loadAddresses();

  let html = '<div class="section-head"><h2>账户设置</h2></div>';

  // Section 1: Basic Info
  html += '<div class="form-section">' +
    '<h3>基本信息</h3>' +
    '<div class="profile-avatar-lg" id="profileAvatar" title="点击上传头像"></div>' +
    '<div class="info-row"><span class="info-label">用户名</span>' +
    '<span class="info-value">' + escapeHtml(user.nickname || user.username || '-') + '</span>' +
    '<button class="btn-success-solid" id="editUsernameBtn" style="margin-left:auto;">修改</button></div>';

  // Email (注册后不可修改)
  html += '<div class="info-row"><span class="info-label">邮箱</span>';
  html += '<span class="info-value">' + escapeHtml(user.email || '未设置') + '</span>';
  html += '</div>';

  html += '<div class="info-row"><span class="info-label">注册时间</span><span class="info-value">' + escapeHtml(user.created_at ? formatTime(new Date(user.created_at).getTime()) : '-') + '</span></div>' +
    '</div>';

  // Section 2: Address Management
  html += '<div class="form-section">' +
    '<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;">' +
    '<h3 style="margin-bottom:0;">地址管理</h3>' +
    '<button class="btn-success-solid" id="addAddrBtn">+ 新增地址</button>' +
    '</div>';
  if (!addresses.length) {
    html += '<div style="text-align:center;padding:20px;color:var(--text-dim);font-size:13px;">暂无地址</div>';
  } else {
    addresses.forEach(function(addr: Address, i: number) {
      const addrName = addr.name ? escapeHtml(addr.name) : '';
      const addrPhone = addr.phone ? escapeHtml(addr.phone) : '';
      const addrRegion = addr.province ? (escapeHtml(addr.province) + ' ' + escapeHtml(addr.city) + ' ' + escapeHtml(addr.district)) : escapeHtml(addr.address || '');
      const addrDetail = addr.detail ? escapeHtml(addr.detail) : (addr.province ? '' : '');
      html += '<div class="address-item">' +
        '<div class="address-info">' +
        '<div class="addr-name">' + (addrName || addrRegion) + (addrName && addrPhone ? ' · ' + addrPhone : '') + (addr.isDefault ? '<span class="default-badge">默认</span>' : '') + '</div>' +
        '<div class="addr-detail">' + (addrName ? addrRegion + (addrDetail ? ' ' + addrDetail : '') : (addrDetail || addrRegion)) + '</div>' +
        '</div>' +
        '<div style="display:flex;gap:6px;flex-shrink:0;">' +
        (!addr.isDefault ? '<button class="btn btn-sm set-default-btn" data-idx="' + i + '">设为默认</button>' : '') +
        '<button class="btn btn-sm edit-addr-btn" data-idx="' + i + '">编辑</button>' +
        '<button class="btn btn-sm btn-danger del-addr-btn" data-idx="' + i + '">删除</button>' +
        '</div></div>';
    });
  }
  html += '</div>';

  // Section 3: Security Settings
  html += '<div class="form-section">' +
    '<h3>安全设置</h3>' +
    '<div class="setting-row">' +
    '<div class="setting-text">' +
    '<div class="setting-title">登录密码</div>' +
    '<div class="setting-desc">安全性高的密码可以使账号更安全，建议您定期更换密码</div>' +
    '</div>' +
    '<button class="btn-success-solid" id="changePwdBtn">修改</button>' +
    '</div>' +
    '<div class="setting-row">' +
    '<div class="setting-text">' +
    '<div class="setting-title">注销账号</div>' +
    '<div class="setting-desc">如果您不再使用此账号，可以将其注销。账号成功注销后，其下所有服务、数据及隐私信息将会被删除并将无法恢复。</div>' +
    '</div>' +
    '<button class="btn-danger-solid" id="deleteAccountBtn">注销</button>' +
    '</div>' +
    '</div>';

  container.innerHTML = html;

  // Populate avatar with DOM API (safe from HTML injection)
  let avatarEl = document.getElementById('profileAvatar');
  if (avatarEl) {
    const avatarUrl = user.avatar_url || user.avatar;
    if (avatarUrl) {
      const img = document.createElement('img');
      img.src = avatarUrl;
      img.alt = '';
      avatarEl.appendChild(img);
    } else {
      avatarEl.textContent = (user.nickname || user.username || '?').charAt(0).toUpperCase();
    }
  }

  // Avatar upload
  avatarEl = document.getElementById('profileAvatar');
  if (avatarEl) {
    avatarEl.addEventListener('click', function() {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/jpeg,image/png,image/gif,image/webp,image/bmp';
      input.addEventListener('change', function() {
        const file = input.files![0];
        if (!file) return;
        const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
        if (allowed.indexOf(file.type) === -1) {
          alert('不支持此图片格式。仅支持 JPG/JPEG、PNG、GIF、WebP、BMP 格式。');
          return;
        }
        const formData = new FormData();
        formData.append('file', file);
        fetch('/api/upload', {
          method: 'POST',
          credentials: 'include',
          body: formData
        }).then(function(resp) {
          if (!resp.ok) {
            return resp.json().catch(function() { return {}; }).then(function(err: { message?: string }) {
              throw new Error(err.message || '头像上传失败');
            });
          }
          return resp.json();
        }).then(function(uploaded: { url: string }) {
          return fetch('/api/auth/me', {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ avatar_url: uploaded.url })
          }).then(function(resp) {
            if (!resp.ok) {
              return resp.json().catch(function() { return {}; }).then(function(err: { message?: string }) {
                throw new Error(err.message || '头像保存失败');
              });
            }
            return resp.json();
          });
        }).then(function(result: { user: User }) {
          user.avatar_url = result.user.avatar_url;
          user.avatar = result.user.avatar_url;
          localStorage.setItem('currentUser', JSON.stringify(user));
          const userInList = users.find(function(u) { return u.username === user.username || u.id === user.id; });
          if (userInList) {
            userInList.avatar_url = result.user.avatar_url;
            userInList.avatar = result.user.avatar_url;
          }
          localStorage.setItem('registeredUsers_' + (user.id || 'anonymous'), JSON.stringify(users));
          updateAvatarUI(result.user);
          renderView('profile');
        }).catch(function(err) {
          showWarning(err.message || '头像上传失败，请稍后重试');
        });
      });
      input.click();
    });
  }

  // Username edit
  const usernameBtn = document.getElementById('editUsernameBtn');
  if (usernameBtn) {
    usernameBtn.addEventListener('click', async function() {
      const currentName = user.nickname || user.username || '';
      const val = await showPrompt('修改用户名', currentName);
      if (val === null || val === currentName) return;
      if (!val.trim()) { alert('用户名不能为空'); return; }
      user.nickname = val.trim();
      user.username = val.trim();
      localStorage.setItem('currentUser', JSON.stringify(user));
      const userInList = users.find(function(u) { return u.username === user.username || u.id === user.id; });
      if (userInList) {
        userInList.nickname = user.nickname;
        userInList.username = user.username;
      }
      localStorage.setItem('registeredUsers_' + (user.id || 'anonymous'), JSON.stringify(users));
      // Sync to backend
      fetch('/api/auth/me', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nickname: user.nickname })
      }).catch(function() {});
      renderView('profile');
    });
  }

  // Change password
  document.getElementById('changePwdBtn')!.addEventListener('click', async function() {
    const oldPwd = await showPrompt('请输入旧密码', '', true);
    if (!oldPwd) return;
    const newPwd = await showPrompt('请输入新密码（至少6位）', '', true);
    if (!newPwd || newPwd.length < 6) { alert('密码至少6位'); return; }
    try {
      const resp = await fetch('/api/auth/change-password', {
        method: 'PUT',
        credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ oldPassword: oldPwd, newPassword: newPwd })
      });
      const data = await resp.json();
      if (!resp.ok) { alert(data.message || '修改失败'); return; }
      showToast('密码已修改');
    } catch(e) { alert('网络错误'); }
  });

  // Delete account
  document.getElementById('deleteAccountBtn')!.addEventListener('click', async function() {
    let ok = await showConfirm('注销账号', '账号注销后，所有数据将被永久删除且无法恢复。确定要注销吗？');
    if (!ok) return;
    ok = await showConfirm('再次确认', '真的要注销账号吗？此操作不可撤销。');
    if (!ok) return;

    // 调用后端删除账号
    const resp = await fetch('/api/auth/me', { method: 'DELETE', credentials: 'include' });
    if (!resp.ok) {
      const err = await resp.json().catch(function() { return {}; }) as { message?: string };
      alert(err.message || '注销失败，请稍后重试');
      return;
    }

    clearLocalAccountData(user.id || 'anonymous', true);
    alert('账号已注销。感谢使用 Memoria 往昔。');
    window.location.href = 'login.html';
  });

  // Address management (server-first, localStorage cache)
  document.getElementById('addAddrBtn')!.addEventListener('click', async function() {
    if (!_showAddressModal) return;
    const result = await _showAddressModal(null);
    if (!result) return;
    try {
      const addrs = loadAddresses();
      const addr = await createAddressOnServer({
        name: result.name, phone: result.phone,
        province: result.province, city: result.city, district: result.district,
        detail: result.detail, isDefault: addrs.length === 0
      });
      addrs.push(addr);
      saveAddressesToCache(addrs);
      renderView('profile');
    } catch (e: any) {
      showToast('保存地址失败: ' + (e.message || '服务器错误'), true);
    }
  });

  container.querySelectorAll('.edit-addr-btn').forEach(function(btn) {
    btn.addEventListener('click', async function() {
      if (!_showAddressModal) return;
      const i = parseInt((this as HTMLElement).getAttribute('data-idx') || '0');
      const addrs = loadAddresses();
      const addr = addrs[i];
      if (!addr) return;
      const current = { name: addr.name || '', phone: addr.phone || '', province: addr.province || '', city: addr.city || '', district: addr.district || '', detail: addr.detail || addr.address || '' };
      const result = await _showAddressModal(current);
      if (!result) return;
      if (!addr.id) {
        showToast('地址数据正在同步中，请稍后再试', true);
        return;
      }
      try {
        await updateAddressOnServer(addr.id, {
          name: result.name, phone: result.phone,
          province: result.province, city: result.city, district: result.district,
          detail: result.detail
        });
        addr.name = result.name;
        addr.phone = result.phone;
        addr.province = result.province;
        addr.city = result.city;
        addr.district = result.district;
        addr.detail = result.detail;
        delete addr.address;
        saveAddressesToCache(addrs);
        renderView('profile');
      } catch (e: any) {
        showToast('更新地址失败: ' + (e.message || '服务器错误'), true);
      }
    });
  });

  container.querySelectorAll('.del-addr-btn').forEach(function(btn) {
    btn.addEventListener('click', async function() {
      const ok = await showConfirm('删除地址', '确定删除这个地址吗？');
      if (!ok) return;
      const i = parseInt((this as HTMLElement).getAttribute('data-idx') || '0');
      const addrs = loadAddresses();
      const addr = addrs[i];
      if (!addr) return;
      if (!addr.id) {
        showToast('地址数据正在同步中，请稍后再试', true);
        return;
      }
      try {
        const wasDefault = addr.isDefault;
        await deleteAddressOnServer(addr.id);
        addrs.splice(i, 1);
        // If we deleted the default address and others remain, promote the first one
        if (wasDefault && addrs.length > 0 && addrs[0].id) {
          addrs[0].isDefault = true;
          updateAddressOnServer(addrs[0].id, { is_default: true }).catch(function(e) {
            console.warn('Failed to promote default address:', e);
          });
        }
        saveAddressesToCache(addrs);
        renderView('profile');
      } catch (e: any) {
        showToast('删除地址失败: ' + (e.message || '服务器错误'), true);
      }
    });
  });

  container.querySelectorAll('.set-default-btn').forEach(function(btn) {
    btn.addEventListener('click', async function() {
      const i = parseInt((this as HTMLElement).getAttribute('data-idx') || '0');
      const addrs = loadAddresses();
      const target = addrs[i];
      if (!target || !target.id) {
        showToast('地址数据正在同步中，请稍后再试', true);
        return;
      }
      // Save old defaults to revert on failure
      const oldDefaults = addrs.map(function(a: Address) { return { id: a.id, isDefault: a.isDefault || false }; });
      try {
        // Update server first — all addresses at once
        for (var j = 0; j < addrs.length; j++) {
          if (addrs[j].id && addrs[j].isDefault !== (j === i)) {
            await updateAddressOnServer(addrs[j].id!, { is_default: j === i });
          }
        }
        // Server succeeded — update local cache
        addrs.forEach(function(a: Address, idx: number) { a.isDefault = (idx === i); });
        saveAddressesToCache(addrs);
        renderView('profile');
      } catch (e: any) {
        // Revert on failure
        for (var k = 0; k < addrs.length; k++) {
          var old = oldDefaults[k];
          if (old && old.id === addrs[k].id) addrs[k].isDefault = old.isDefault;
        }
        saveAddressesToCache(addrs);
        showToast('设置默认地址失败: ' + (e.message || '服务器错误'), true);
      }
    });
  });
}

// ========================
// View: Help Center
// ========================

export function renderHelp(container: HTMLElement): void {
  const faqs = [
    { q: '产品类型', a: '电子版（PDF 下载）：60 页内免费、可自愿打赏；超出 60 页需打赏任意金额才能下单。下单后即刻下载，适合自己打印或在手机/电脑上翻阅。<br><br>实体相册：8寸方形精装（21cm × 21cm），提供两种内页选择：<br>• <strong>硬壳硬页</strong> — 硬壳硬面，艺术微喷哑光内页。¥119 起含 24 内页，超出每跨页 +¥8，60 页封顶。<br>• <strong>硬壳软页</strong> — 硬壳软面，米色 190 克美华艺术纸。¥119 起含 34 内页，超出每跨页 +¥6，120 页封顶。<br><br>实体相册内页必须偶数。中国大陆包邮，港澳台及海外暂不配送。' },
    { q: '退款政策', a: '<strong>电子版 PDF：</strong>数字商品一经下单支付，除非文件无法下载或文件与预览相册不符，否则不支持退款。请在支付前仔细检查相册内容，编辑器内所见即所得，支付即表示已确认内容无误。如遇下载链接失效、文件损坏等确实无法下载的情况，请联系客服。<br><br><strong>实体相册：</strong>付款后 1 小时内可自助取消订单，超时不支持无理由取消。定制商品不支持无理由退货。退款 1–2 个工作日到账，积分原路返还。' },
    { q: '发货时效', a: '实体相册付款后 3–5 个工作日发货，法定节假日可能顺延。' },
    { q: '支付与开票', a: '支持微信支付，积分最高可抵扣订单金额 20%。需要发票请联系客服提供抬头信息。' },
    { q: '照片与制作', a: '支持 JPG、PNG、HEIC 等常见格式，HEIC 自动转为 JPEG。编辑完全免费，满意后再付费。你的照片仅用于相册制作和订单履约，不会公开展示或用于模板展示。' },
    { q: '售后与质保', a: '实体相册签收后 7 天内如有破损等问题，联系客服并提供外包装照片、问题照片或视频、页码说明，优先免费重印。屏幕与纸张存在合理色差，轻微色差不属于质量问题。' }
  ];
  let html = '<div class="section-head"><h2>常见问题</h2></div><div class="faq-list">';
  faqs.forEach(function(faq) {
    html += '<div class="faq-item" data-faq-toggle>' +
      '<div class="faq-q"><span>' + faq.q + '</span><span class="faq-toggle">+</span></div>' +
      '<div class="faq-a">' + faq.a + '</div></div>';
  });
  html += '</div>';
  container.innerHTML = html;
  // Event delegation for FAQ toggle (replaces inline onclick)
  container.querySelectorAll('[data-faq-toggle]').forEach(function(el) {
    el.addEventListener('click', function() { this.classList.toggle('open'); });
  });
}

// ========================
// View: WeChat Assistant
// ========================

export function renderWeChat(container: HTMLElement): void {
  const html = '<div class="section-head"><h2>联系客服</h2></div>' +
    '<div class="qr-section">' +
    '<img class="qr-image" src="images/assets/企业微信二维码.png" alt="企业微信二维码">' +
    '<h3>扫码添加企微助手</h3>' +
    '<p>获取一对一帮助，解答你的任何问题</p>' +
    '<p style="font-size:11px;color:var(--text-dim);margin-top:4px;">工作日 9:00-18:00 在线</p>' +
    '</div>';
  container.innerHTML = html;
}

// ========================
// View: Credits
// ========================

export function renderCredits(container: HTMLElement): void {
  const html = '<div class="section-head"><h2>我的积分</h2></div>' +
    '<div class="qr-section">' +
    '<div style="font-size:48px;margin-bottom:8px;">💰</div>' +
    '<div style="font-size:28px;font-weight:700;margin-bottom:4px;" id="creditsBalanceLarge">—</div>' +
    '<p style="font-size:13px;color:var(--text-dim);">当前可用积分</p>' +
    '<button class="btn btn-accent" id="creditsHistoryBtn" style="margin-top:16px;">查看积分明细</button>' +
    '</div>' +
    '<div class="section-head" style="margin-top:24px;"><h2>积分说明</h2></div>' +
    '<div class="credits-info-list">' +
    '<p>如何获取：你的模板被他人使用 <b>+100</b> 积分</p>' +
    '<p>如何使用：<b>' + getCreditsPerYuan() + '</b> 积分 = <b>1</b> 元，实体订单最多可抵 <b>20%</b></p>' +
    '</div>';
  container.innerHTML = html;

  document.getElementById('creditsHistoryBtn')!.addEventListener('click', function() {
    if (_showCreditsHistory) _showCreditsHistory();
  });
  refreshCreditsBalance();
}

export function refreshCreditsBalance(): void {
  const el = document.getElementById('creditsBalanceLarge');
  if (!el) return;
  fetch('/api/credits', {
    credentials: 'include'
  }).then(function(r) { return r.json(); }).then(function(data: { balance?: number }) {
    const balance = data.balance || 0;
    el.textContent = balance + ' 积分';
  }).catch(function() {
    el.textContent = '—';
  });
}
